import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'

import { bidIssuerTypeOf } from '~/models/bidIssuerType'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'

/**
 * 1 件の `BiddingSpotApprovalRequest` を通常入札タブの表示行 `SpotBidRow[]` に変換する。
 *
 * - `targetDate` / `approvalState` は親 request から各札へ展開する。
 * - 約定価格/量 (exec*) は BFF の `matched_*` から写す (約定待ち / 不約定では未設定 =
 *   約定列は「—」表示)。 トランシェは backend 非提供のため未設定 (= filter は空集合)。
 *   トランシェ実装は後続 Wave。
 * - `createdAt` は業務上 ms 未満を扱わない監査表示値なので、 `Date` に変換して持つ。
 */
function convertOneRequestToRows(
  req: BiddingSpotApprovalRequest,
): SpotBidRow[] {
  const targetDate =
    req.targetDate !== undefined
      ? new Date(
          req.targetDate.year,
          req.targetDate.month - 1,
          req.targetDate.day,
        )
      : undefined
  return req.spotBids.map((b) => ({
    id: b.id,
    approvalRequestId: req.id,
    areaCode: b.areaCode,
    timeCode: b.timeCode,
    direction: b.direction,
    // 価格種別 (AC-SPOT-006)。 成行札は price が "0" で来るので、 これを落とすと
    // 「0 円の指値」 と区別できなくなる (= 価格レンジ違反として承認申請がブロック
    // され、 承認者にも ¥0.00 の指値としてしか見えない)。
    orderMethod: b.orderMethod,
    priceYenPerKwh: b.priceYenPerKwh?.value ?? '0',
    volumeMwh: b.volumeMwh?.value ?? '0',
    bidStatus: b.bidStatus,
    needsReview: b.needsReview,
    needsReviewSince:
      b.needsReviewSince !== undefined
        ? timestampDate(b.needsReviewSince)
        : undefined,
    jepxBidNo: b.jepxBidNo,
    approvalState: req.approvalState,
    // 送信軸は承認依頼単位 (提出は申請セットの一括処理) なので親から写す。
    // 締切までに JEPX へ届かなかった札は承認軸 SUBMITTED のままなので、
    // 約定率の母集合判定はこの軸を見る必要がある (spotExecutionSummary)。
    sendStatus: req.sendStatus,
    // 失敗理由も同じ親から写す。 sendStatus だけを写すと `SendErrorReasonTooltip` に
    // 理由が渡らず、 送信失敗の理由 tooltip が落ちる
    // (先行実装 convertBlockBidsFromApprovalRequests も 3 つセットで写している)。
    sendErrorCategory: req.sendErrorCategory,
    sendErrorDetail: req.sendErrorDetail,
    issuerType: bidIssuerTypeOf(b.issuerType),
    // トランシェは backend 非提供 (後続 Wave)。
    trancheId: undefined,
    // 約定価格 / 約定量は BFF が返す (bidding/converter.go の convertSpotBid が
    // matched_* を写す)。 約定待ち / 不約定では未設定なので undefined のまま渡す。
    execPriceYenPerKwh: b.matchedPriceYenPerKwh?.value,
    execVolumeMwh: b.matchedVolumeMwh?.value,
    memoJepx: b.memoJepx,
    memoFree: b.memoFree,
    createdAt:
      b.createdAt !== undefined ? timestampDate(b.createdAt) : new Date(0),
    targetDate,
    createdBy: b.createdBy,
    // 受渡契約コード / ワークフローID (AC-BSP-001、 T-74 で proto に追加)。
    deliveryContractCode: b.deliveryContractCode,
    workflowId: b.workflowId,
    origin: b.origin,
    expiryConfirmed: b.expiryConfirmed,
    receiptChecking: b.receiptChecking,
    withdrawable: b.withdrawable,
    // 札ごとの可否は backend が決める (戻るのは申請セットの送信中の札だけ。 AC-BSP-117)。
    returnableToDraft: b.returnableToDraft,
  }))
}

/**
 * 受渡日の全承認案件 (= 追加入札 AC-079 により同一受渡日に複数申請セットが
 * 並存しうる、 AC-082) から札一覧 `SpotBidRow[]` を合算して返す。
 *
 * 各 request の札は `approvalRequestId` / `approvalState` を保持したまま
 * flat に展開される (= 編集・削除は呼び出し側で `approvalState === DRAFT`
 * の行のみ許可する、 AC-066/AC-082b)。 `undefined` 要素 (= 並列 Get が未解決)
 * はスキップする。
 */
export function convertSpotBidsFromApprovalRequest(
  reqs: ReadonlyArray<BiddingSpotApprovalRequest | undefined>,
): SpotBidRow[] {
  return reqs.flatMap((req) =>
    req !== undefined ? convertOneRequestToRows(req) : [],
  )
}
